<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'

const { execute: resetGame, hasHandler: hasResetHandler } = useGameReset()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const { hasPresenterTarget, isFullscreen, toggle: toggleFullscreen } = useGameFullscreen()

const { open: openAbout } = useAboutModal()
const { open: openHelp } = useGameHelpModal()

// Defer the presenter-view check to post-mount so SSR HTML and the initial
// hydration tree always agree (the prerendered page has no query params).
const mounted = ref(false)
onMounted(() => {
  mounted.value = true
})
const isPresenterView = computed(() => mounted.value && route.query.view === 'presenter')

// Presence is supplied by the game page via provideGamePresence(); when
// undefined (older pages, layouts without a presence provider) the FAB
// falls back to its prior behavior with no connection-aware items.
const presence = useGamePresence()

const isPeerPresenter = computed(() =>
  isPresenterView.value && presence?.presenterTransportKind.value === 'peer',
)

// Red dot only fires on the presenter view when we're meant to be paired
// (peer transport) but the connection has dropped. The auto-reconnect
// backoff runs in the background; the dot is the user-visible signal that
// something is wrong before they think to open the menu.
const showDisconnectedBadge = computed(() =>
  isPeerPresenter.value && presence !== undefined && !presence.connected.value,
)

const exitConfirmOpen = ref(false)
const reconnectModalOpen = ref(false)
const pairingStatusModalOpen = ref(false)
const takeControlConfirmOpen = ref(false)

function confirmExit() {
  exitConfirmOpen.value = false
  // Broadcast presenter is a popup window opened from the controller —
  // close it so the user lands back on the still-running parent controller
  // rather than navigating the popup itself to /games and orphaning a
  // stray tab on a different page.
  if (
    isPresenterView.value
    && presence?.presenterTransportKind.value === 'broadcast'
    && import.meta.client
    && typeof window !== 'undefined'
    && window.opener
  ) {
    window.close()
    return
  }
  void router.push('/games')
}

function confirmTakeControl() {
  takeControlConfirmOpen.value = false
  presence?.takeControl?.()
}

const presenterConnectionLabel = computed(() => {
  if (!presence) {
    return ''
  }
  if (presence.connected.value) {
    return t('gameFab.presenterConnection.connected')
  }
  if (presence.connecting.value) {
    return t('gameFab.presenterConnection.connecting')
  }
  return t('gameFab.presenterConnection.disconnected')
})

const presenterConnectionIcon = computed(() => {
  if (!presence) {
    return 'mdi:lan-disconnect'
  }
  if (presence.connected.value) {
    return 'mdi:lan-connect'
  }
  return 'mdi:lan-disconnect'
})

const controllerPairingLabel = computed(() => {
  if (!presence) {
    return t('gameFab.controllerPairing.label')
  }
  if (!presence.pairingCode.value) {
    return t('gameFab.controllerPairing.notReady')
  }
  const count = presence.peerPresentersCount.value
  if (count === 0) {
    return t('gameFab.controllerPairing.waiting')
  }
  return count === 1
    ? t('gameFab.controllerPairing.connectedOne', { count })
    : t('gameFab.controllerPairing.connectedMany', { count })
})

// ── Item builders ──────────────────────────────────────
const fullscreenItem = computed<DropdownMenuItem>(() => ({
  icon: isFullscreen.value ? 'mdi:fullscreen-exit' : 'mdi:fullscreen',
  label: hasPresenterTarget.value
    ? (isFullscreen.value ? t('gameFab.fullscreen.exitPresenter') : t('gameFab.fullscreen.enterPresenter'))
    : (isFullscreen.value ? t('gameFab.fullscreen.exit') : t('gameFab.fullscreen.enter')),
  onSelect() {
    toggleFullscreen()
  },
}))

const helpItem = computed<DropdownMenuItem>(() => ({
  icon: 'mdi:help-circle-outline',
  label: t('gameFab.help'),
  onSelect() {
    openHelp('game_fab')
  },
}))

const aboutItem = computed<DropdownMenuItem>(() => ({
  icon: 'mdi:information-outline',
  label: t('gameFab.about'),
  onSelect() {
    openAbout('game_fab')
  },
}))

const exitToLibraryItem = computed<DropdownMenuItem>(() => ({
  color: 'error' as const,
  icon: 'mdi:home-outline',
  label: t('gameFab.exitLibrary'),
  onSelect() {
    exitConfirmOpen.value = true
  },
}))

const presenterConnectionItem = computed<DropdownMenuItem>(() => ({
  icon: presenterConnectionIcon.value,
  label: presenterConnectionLabel.value,
  onSelect() {
    reconnectModalOpen.value = true
  },
}))

const controllerPairingItem = computed<DropdownMenuItem>(() => ({
  icon: 'mdi:cellphone-link',
  label: controllerPairingLabel.value,
  onSelect() {
    pairingStatusModalOpen.value = true
  },
}))

const takeControlItem = computed<DropdownMenuItem>(() => ({
  color: 'warning' as const,
  icon: 'mdi:remote-desktop',
  label: t('gameFab.presenterTakeControl'),
  onSelect() {
    takeControlConfirmOpen.value = true
  },
}))

const items = computed<DropdownMenuItem[][]>(() => {
  if (isPresenterView.value) {
    // Presenter view: drop "Reset Game" (controller-only). When peer-paired
    // we surface connection status (with a click-through to the reconnect
    // modal) and a "Take control" handoff. Exit always confirms and then
    // navigates to the library, mirroring the controller's exit.
    const groups: DropdownMenuItem[][] = [ [ fullscreenItem.value ] ]
    if (isPeerPresenter.value && presence) {
      groups.push([ presenterConnectionItem.value ])
    }
    groups.push([ helpItem.value, aboutItem.value ])
    if (isPeerPresenter.value && presence?.takeControl) {
      groups.push([ takeControlItem.value ])
    }
    groups.push([ exitToLibraryItem.value ])
    return groups
  }

  const groups: DropdownMenuItem[][] = [ [ fullscreenItem.value ] ]
  if (presence && presence.viewMode.value === 'controller') {
    groups.push([ controllerPairingItem.value ])
  }
  groups.push([ helpItem.value, aboutItem.value ])
  if (hasResetHandler.value) {
    groups.push([
      {
        color: 'warning' as const,
        icon: 'mdi:restart',
        label: t('gameFab.resetGame'),
        onSelect() {
          resetGame()
        },
      },
    ])
  }
  groups.push([ exitToLibraryItem.value ])
  return groups
})

function retryPresenterConnection() {
  presence?.retryConnection()
}
</script>

<template>
  <div class="fixed bottom-4 left-4 z-50">
    <UDropdownMenu
      :items="items"
      :content="{ side: 'top', align: 'start', sideOffset: 8 }"
      :modal="false"
      :ui="{ content: 'w-56' }"
    >
      <div class="relative">
        <SfgGameCornerButton
          icon="mdi:cog-outline"
          aria-label="Game options"
        />
        <span
          v-if="showDisconnectedBadge"
          class="absolute -right-0.5 -top-0.5 block size-3 rounded-full border-2 border-default bg-red-500"
          :aria-label="t('gameFab.presenterConnection.disconnected')"
        />
      </div>
    </UDropdownMenu>

    <!-- Exit confirmation (shared by controller and presenter views) -->
    <UModal
      v-model:open="exitConfirmOpen"
      :ui="{
        overlay: 'sfg-about-overlay',
        content: 'rotate-[-0.4deg]',
      }"
    >
      <template #content>
        <div class="bg-brand-ink text-brand-chalk rounded-card overflow-hidden">
          <div class="flex flex-col items-center gap-5 p-8 sm:p-10 text-center">
            <UIcon
              name="mdi:home-outline"
              class="size-12 text-brand-yellow shrink-0"
            />
            <h2 class="font-editorial italic font-black text-[28px] sm:text-[32px] leading-none">
              {{ t('gameFab.exitConfirm.title') }}
            </h2>
            <p class="font-body text-body leading-relaxed max-w-md">
              {{ t('gameFab.exitConfirm.body') }}
            </p>
            <div class="flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto">
              <SfgButton
                variant="ghost"
                size="sm"
                @click="exitConfirmOpen = false"
              >
                {{ t('gameFab.exitConfirm.cancel') }}
              </SfgButton>
              <SfgButton
                variant="primary"
                size="sm"
                @click="confirmExit"
              >
                {{ t('gameFab.exitConfirm.confirm') }}
              </SfgButton>
            </div>
          </div>
        </div>
      </template>
    </UModal>

    <!-- Take control confirmation (presenter -> controller in this tab) -->
    <UModal
      v-model:open="takeControlConfirmOpen"
      :ui="{
        overlay: 'sfg-about-overlay',
        content: 'rotate-[-0.4deg]',
      }"
    >
      <template #content>
        <div class="bg-brand-ink text-brand-chalk rounded-card overflow-hidden">
          <div class="flex flex-col items-center gap-5 p-8 sm:p-10 text-center">
            <UIcon
              name="mdi:remote-desktop"
              class="size-12 text-brand-yellow shrink-0"
            />
            <h2 class="font-editorial italic font-black text-[28px] sm:text-[32px] leading-none">
              {{ t('gameFab.takeControlConfirm.title') }}
            </h2>
            <p class="font-body text-body leading-relaxed max-w-md">
              {{ t('gameFab.takeControlConfirm.body') }}
            </p>
            <div class="flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto">
              <SfgButton
                variant="ghost"
                size="sm"
                @click="takeControlConfirmOpen = false"
              >
                {{ t('gameFab.takeControlConfirm.cancel') }}
              </SfgButton>
              <SfgButton
                variant="primary"
                size="sm"
                @click="confirmTakeControl"
              >
                {{ t('gameFab.takeControlConfirm.confirm') }}
              </SfgButton>
            </div>
          </div>
        </div>
      </template>
    </UModal>

    <!-- Presenter reconnect modal: pairing code + live connection state -->
    <UModal
      v-if="presence && isPeerPresenter"
      v-model:open="reconnectModalOpen"
      :ui="{
        overlay: 'sfg-about-overlay',
        content: 'rotate-[-0.4deg]',
      }"
    >
      <template #content>
        <div class="bg-brand-ink text-brand-chalk rounded-card overflow-hidden">
          <div class="flex flex-col items-center gap-5 p-8 sm:p-10 text-center">
            <UIcon
              :name="presence.connected.value ? 'mdi:lan-connect' : 'mdi:lan-disconnect'"
              :class="[
                'size-12 shrink-0',
                presence.connected.value ? 'text-brand-yellow' : 'text-red-400',
              ]"
            />
            <h2 class="font-editorial italic font-black text-[28px] sm:text-[32px] leading-none">
              {{ t('gameFab.reconnectModal.title') }}
            </h2>
            <div class="flex flex-col gap-2 items-center">
              <span class="font-body text-xs uppercase tracking-wide opacity-70">
                {{ t('gameFab.reconnectModal.pairingCodeLabel') }}
              </span>
              <span class="font-mono text-3xl font-black tracking-[0.2em]">
                {{ presence.presenterTargetCode.value || '——————' }}
              </span>
            </div>
            <div class="flex flex-col gap-1">
              <span
                class="font-body text-base font-semibold"
                :class="[
                  presence.connected.value ? 'text-brand-yellow' : 'text-red-300',
                ]"
              >
                {{ presence.connected.value
                  ? t('gameFab.reconnectModal.statusConnected')
                  : (presence.connecting.value
                    ? t('gameFab.reconnectModal.statusConnecting')
                    : t('gameFab.reconnectModal.statusFailed')) }}
              </span>
              <p
                v-if="presence.lastError.value && !presence.connected.value"
                class="font-body text-sm text-red-200/80"
              >
                {{ presence.lastError.value }}
              </p>
              <p
                v-else-if="!presence.connected.value"
                class="font-body text-sm opacity-70 max-w-md"
              >
                {{ t('gameFab.reconnectModal.helpText') }}
              </p>
            </div>
            <div class="flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto">
              <SfgButton
                variant="ghost"
                size="sm"
                @click="reconnectModalOpen = false"
              >
                {{ t('gameFab.reconnectModal.closeButton') }}
              </SfgButton>
              <SfgButton
                v-if="!presence.connected.value"
                variant="primary"
                size="sm"
                @click="retryPresenterConnection"
              >
                {{ t('gameFab.reconnectModal.retryButton') }}
              </SfgButton>
            </div>
          </div>
        </div>
      </template>
    </UModal>

    <!-- Controller pairing-status modal: code + connected presenter count -->
    <UModal
      v-if="presence && presence.viewMode.value === 'controller'"
      v-model:open="pairingStatusModalOpen"
      :ui="{
        overlay: 'sfg-about-overlay',
        content: 'rotate-[-0.4deg]',
      }"
    >
      <template #content>
        <div class="bg-brand-ink text-brand-chalk rounded-card overflow-hidden">
          <div class="flex flex-col items-center gap-5 p-8 sm:p-10 text-center">
            <UIcon
              name="mdi:cellphone-link"
              class="size-12 text-brand-yellow shrink-0"
            />
            <h2 class="font-editorial italic font-black text-[28px] sm:text-[32px] leading-none">
              {{ t('gameFab.pairingStatusModal.title') }}
            </h2>
            <div class="flex flex-col gap-2 items-center">
              <span class="font-body text-xs uppercase tracking-wide opacity-70">
                {{ t('gameFab.pairingStatusModal.pairingCodeLabel') }}
              </span>
              <span class="font-mono text-3xl font-black tracking-[0.2em]">
                {{ presence.pairingCode.value || '——————' }}
              </span>
            </div>
            <span class="font-body text-base font-semibold text-brand-yellow">
              {{ controllerPairingLabel }}
            </span>
            <p
              v-if="presence.lastError.value"
              class="font-body text-sm text-red-200/80 max-w-md"
            >
              {{ presence.lastError.value }}
            </p>
            <p
              v-else
              class="font-body text-sm opacity-70 max-w-md"
            >
              {{ t('gameFab.pairingStatusModal.helpText') }}
            </p>
            <SfgButton
              variant="ghost"
              size="sm"
              @click="pairingStatusModalOpen = false"
            >
              {{ t('gameFab.pairingStatusModal.closeButton') }}
            </SfgButton>
          </div>
        </div>
      </template>
    </UModal>
  </div>
</template>
